import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/fire';
import moment from 'moment-timezone';
import {
  FaCalendarAlt,
  FaCalendarWeek,
  FaCalendarDay,
  FaUserCircle,
  FaDownload,
  FaSearch,
  FaTimes,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

const BookingDetails = ({ theme = 'light' }) => {
  const [bookingsData, setBookingsData] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(moment().tz('Pacific/Auckland'));
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [selectedDate, viewMode]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const classScheduleSnapshot = await getDocs(collection(db, 'classSchedule'));
      const allBookings = [];

      for (const classDoc of classScheduleSnapshot.docs) {
        const classData = classDoc.data();
        const classId = classDoc.id;

        if (classData.bookings) {
          for (const [date, booking] of Object.entries(classData.bookings)) {
            const bookingDate = moment(date, 'YYYY-MM-DD');
            
            // Filter based on view mode
            if (shouldIncludeDate(bookingDate)) {
              // Fetch member details for each booking
              const memberPromises = booking.memberIds.map(async (memberId) => {
                try {
                  const memberDoc = await getDoc(doc(db, 'members', memberId));
                  if (memberDoc.exists()) {
                    const memberData = memberDoc.data();
                    return {
                      memberId,
                      memberName: `${memberData.name || ''} ${memberData.lastName || ''}`.trim(),
                      memberEmail: memberData.email || '',
                      memberPhone: memberData.phone || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching member ${memberId}:`, error);
                }
                return null;
              });

              const members = (await Promise.all(memberPromises)).filter(Boolean);

              if (members.length > 0) {
                allBookings.push({
                  classId,
                  className: classData.className,
                  date: date,
                  dayOfWeek: bookingDate.format('dddd'),
                  startTime: classData.startTime,
                  endTime: classData.endTime,
                  members,
                  bookingCount: members.length,
                  availableSpots: classData.availableSpots || 0
                });
              }
            }
          }
        }
      }

      // Sort by date and time
      allBookings.sort((a, b) => {
        const dateCompare = moment(a.date).diff(moment(b.date));
        if (dateCompare !== 0) return dateCompare;
        return moment(a.startTime, 'HH:mm').diff(moment(b.startTime, 'HH:mm'));
      });

      setBookingsData(allBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  };

  const shouldIncludeDate = (bookingDate) => {
    const current = moment(selectedDate);
    
    if (viewMode === 'day') {
      return bookingDate.isSame(current, 'day');
    } else if (viewMode === 'week') {
      const startOfWeek = current.clone().startOf('week');
      const endOfWeek = current.clone().endOf('week');
      return bookingDate.isBetween(startOfWeek, endOfWeek, 'day', '[]');
    } else if (viewMode === 'month') {
      return bookingDate.isSame(current, 'month');
    }
    return false;
  };

  const changeDate = (direction) => {
    const newDate = selectedDate.clone();
    if (viewMode === 'day') {
      newDate.add(direction, 'days');
    } else if (viewMode === 'week') {
      newDate.add(direction, 'weeks');
    } else if (viewMode === 'month') {
      newDate.add(direction, 'months');
    }
    setSelectedDate(newDate);
  };

  const getDateRangeDisplay = () => {
    if (viewMode === 'day') {
      return selectedDate.format('MMMM D, YYYY');
    } else if (viewMode === 'week') {
      const startOfWeek = selectedDate.clone().startOf('week');
      const endOfWeek = selectedDate.clone().endOf('week');
      return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;
    } else if (viewMode === 'month') {
      return selectedDate.format('MMMM YYYY');
    }
  };

  const filteredBookings = bookingsData.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.className.toLowerCase().includes(search) ||
      booking.members.some(m => m.memberName.toLowerCase().includes(search))
    );
  });

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const exportCSV = () => {
    const rows = [['Class','Date','Time','Member Name','Email','Phone']];
    filteredBookings.forEach(b => {
      b.members.forEach(m => {
        rows.push([
          b.className,
          moment(b.date).format('YYYY-MM-DD'),
          `${b.startTime} - ${b.endTime}`,
          m.memberName,
          m.memberEmail,
          m.memberPhone
        ]);
      });
    });

    const csvContent = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bookings_${moment().format('YYYYMMDD_HHmm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getTotalBookings = () => {
    return filteredBookings.reduce((sum, booking) => sum + booking.bookingCount, 0);
  };

  return (
    <div className={`p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
          {/* <div>
            <h1 className="text-l sm:text-xl lg:text-xl font-extrabold text-gray-900 mb-1">Member Class Booking</h1>
          </div> */}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <div className={`inline-flex items-center border rounded-lg shadow-sm overflow-hidden ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
            }`}>
              <button
                onClick={() => changeDate(-1)}
                className={`px-2 sm:px-3 py-2 text-gray-600 flex-shrink-0 ${
                  theme === 'dark' ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-50 text-gray-600'
                }`}
                aria-label="Previous"
              >
                <FaChevronLeft className="text-sm sm:text-base" />
              </button>
              <div className={`px-2 sm:px-4 py-2 font-semibold text-xs sm:text-sm md:text-base text-center min-w-0 truncate ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>{getDateRangeDisplay()}</div>
              <button
                onClick={() => changeDate(1)}
                className={`px-2 sm:px-3 py-2 flex-shrink-0 ${
                  theme === 'dark' ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-50 text-gray-600'
                }`}
                aria-label="Next"
              >
                <FaChevronRight className="text-sm sm:text-base" />
              </button>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => setViewMode('day')}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${viewMode === 'day' ? 'bg-indigo-600 text-white' : theme === 'dark' ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
              >Day</button>
              <button
                onClick={() => setViewMode('week')}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${viewMode === 'week' ? 'bg-indigo-600 text-white' : theme === 'dark' ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
              >Week</button>
              <button
                onClick={() => setViewMode('month')}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${viewMode === 'month' ? 'bg-indigo-600 text-white' : theme === 'dark' ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
              >Month</button>
            </div>
          </div>
        </div>

        {/* Controls row */}
        <div className="mt-3 sm:mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 items-start lg:items-center">
          <div className="relative lg:col-span-2">
            <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`}><FaSearch className="text-sm" /></span>
            <input
              type="text"
              placeholder="Search classes or members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-3 text-sm sm:text-base border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'
              }`}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}>
                <FaTimes className="text-sm" />
              </button>
            )}
          </div>

          <div className="flex justify-start lg:justify-end gap-2 sm:gap-3">
            <div className={`p-2 sm:p-3 rounded-lg border shadow-sm text-center flex-1 sm:flex-none ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
            }`}>
              <div className={`text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>Classes</div>
              <div className={`text-base sm:text-lg font-bold ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>{filteredBookings.length}</div>
            </div>
            <div className={`p-2 sm:p-3 rounded-lg border shadow-sm text-center flex-1 sm:flex-none ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
            }`}>
              <div className={`text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>Bookings</div>
              <div className={`text-base sm:text-lg font-bold ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>{getTotalBookings()}</div>
            </div>
            <button onClick={exportCSV} className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 text-xs sm:text-sm whitespace-nowrap flex-1 sm:flex-none">
              <FaDownload className="text-xs sm:text-sm" /> <span className="hidden sm:inline">Export</span><span className="sm:hidden">CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading bookings...</p>
        </div>
      )}

      {/* No Data State */}
      {!loading && filteredBookings.length === 0 && (
        <div className={`text-center py-12 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>No bookings found for this period</p>
        </div>
      )}

      {/* Bookings List */}
      {!loading && filteredBookings.length > 0 && (
        <div className="space-y-3 sm:space-y-4">
          {filteredBookings.map((booking, index) => (
            <div key={`${booking.classId}-${booking.date}-${index}`} className={`border rounded-lg shadow-sm overflow-hidden ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
            }`}>
              {/* Class Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row sm:flex-wrap justify-between items-start sm:items-center gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold truncate">{booking.className}</h3>
                    <p className="text-xs sm:text-sm text-blue-100">{booking.dayOfWeek}</p>
                    <p className="mt-1 text-white text-xs sm:text-sm md:text-base font-semibold">{moment(booking.date).format('MMMM D, YYYY')}</p>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <p className="text-sm sm:text-base lg:text-lg font-semibold">{booking.startTime} - {booking.endTime}</p>
                    <p className="text-xs sm:text-sm text-blue-100">
                      {booking.bookingCount} / {booking.availableSpots} spots booked
                    </p>
                  </div>
                </div>
              </div>

              {/* Members Table */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className={`${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-50'
                  }`}>
                    <tr className={`text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left">#</th>
                      <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left">Member</th>
                      <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left">Email</th>
                      <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left">Phone</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${
                    theme === 'dark' ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-200'
                  }`}>
                    {booking.members.map((member, memberIndex) => (
                      <tr key={member.memberId} className={`${
                        theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-50'
                      }`}>
                        <td className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}>{memberIndex + 1}</td>
                        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex-shrink-0 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-xs sm:text-sm">
                              {getInitials(member.memberName)}
                            </div>
                            <div className="text-xs sm:text-sm min-w-0">
                              <div className={`font-medium truncate ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                              }`}>{member.memberName}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className={`text-xs sm:text-sm truncate ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                          }`}>{member.memberEmail}</div>
                        </td>
                        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                          }`}>{member.memberPhone}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
